import { useEffect, useState } from "react";
import { put } from "@common/api/apiShared";
import { useDotLegalMutation, useDotLegalQuery, useDotLegalQueryClient } from "@common/hooks/dotLegalReactQuery";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { getContractQueryKey } from "../Contract.hooks";
import { IArchiveContractDialogProps } from "./ArchiveContractDialog";

export interface ContractObligationRow {
    id: string;
    name: string;
}

export interface ArchiveContractRequest {
    endDate: Date | null;
    reason: string;
    obligationIds: Array<string>;
}

export function useArchiveContractDialog(props: IArchiveContractDialogProps) {
    const snackbar = useDotLegalSnackbar();
    const { translateString } = useTranslation();
    const queryClient = useDotLegalQueryClient();

    const [endDate, setEndDate] = useState<Date | null>(props.contractEndDate ?? null);
    const [reason, setReason] = useState("");
    const [selectedObligationIds, setSelectedObligationIds] = useState<Array<string>>([]);

    const obligationsQuery = useDotLegalQuery<Array<ContractObligationRow>>(`/contracts/${props.contractId}/archive/recurring-obligations`);

    useEffect(() => {
        if (obligationsQuery.data) {
            setSelectedObligationIds(obligationsQuery.data.map((x) => x.id));
        }
    }, [obligationsQuery.data]);

    const archiveMutation = useDotLegalMutation(archiveContractApi);

    function archiveContract() {
        archiveMutation.mutateAsync(
            { endDate, reason, obligationIds: selectedObligationIds },
            {
                onSuccess: () => {
                    queryClient.refetchQueries(getContractQueryKey(props.contractId));
                    snackbar.show(translateString("contractArchived"));
                    props.onArchived();
                },
            }
        );
    }

    function archiveContractApi(request: ArchiveContractRequest) {
        return put(`/contracts/${props.contractId}/archive`, request);
    }

    const showObligations = !obligationsQuery.isLoading && (obligationsQuery.data?.length ?? 0) > 0 && endDate !== null;

    function onSelectAllObligations(selected: Array<ContractObligationRow>) {
        setSelectedObligationIds(selected.map((x) => x.id));
    }

    function isObligationChecked(id: string) {
        return selectedObligationIds.some((x) => x === id);
    }

    function onObligationCheckboxClick(row: ContractObligationRow) {
        if (isObligationChecked(row.id)) {
            setSelectedObligationIds((prev) => prev.filter((x) => x !== row.id));
        } else {
            setSelectedObligationIds((prev) => [...prev, row.id]);
        }
    }

    function getHeaderCheckBoxState(): "true" | "false" | "indeterminate" {
        if (selectedObligationIds.length === 0) return "false";
        return obligationsQuery.data?.length === selectedObligationIds.length ? "true" : "indeterminate";
    }

    return {
        endDate,
        setEndDate,
        reason,
        setReason,
        archiveContract,
        inProgress: archiveMutation.isLoading,
        obligations: obligationsQuery.data,
        isLoadingObligations: obligationsQuery.isLoading,
        showObligations,
        selectedObligationIds,
        onSelectAllObligations,
        isObligationChecked,
        onObligationCheckboxClick,
        getHeaderCheckBoxState,
    };
}
